import React, { useState, useCallback, useMemo } from "react";
import AssignDriverModal from "./AssignDriverModal";
import "../styles/TaskSidebar.css";

export const statusColors = {
  "NON ASSIGNE": "#b0b0b0",
  SUCCESS: "#4caf50",
  FAILED: "#ff0000",
  START: "#1e90ff",
  ACCEPTE: "#800080",
  ASSIGNE: "#ff8c00",
  "IN PROGRESS": "#0f056b",
  0: "#ff0000",
  1: "#ff0000",
  2: "#ffa500",
  3: "#0000ff",
  4: "#00cc00",
  5: "#4682b4",
  UNKNOWN: "#808080",
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;

  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance.toFixed(2);
};

const calculateTimeElapsed = (createdAt) => {
  if (!createdAt) return "N/A";
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now - created;
  const diffMins = Math.floor(diffMs / 1000 / 60);
  return `${diffMins} min`;
};

const sendRestaurantNotification = async (orderId) => {
  try {
    const formData = new URLSearchParams();
    formData.append("order_id", orderId);

    const response = await fetch("https://lalivraison.ma/Livreurxcs/pushnotif.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const result = await response.json();
    if (result.success) {
      alert("Notification sent to restaurant successfully!");
    } else {
      console.error("Notification failed:", result.message, result.details);
      throw new Error(result.message || "Failed to send notification");
    }
  } catch (error) {
    console.error("Error sending notification:", error.message);
    alert(`Failed to send notification: ${error.message}`);
  }
};

const truncateAddress = (address, wordLimit = 3) => {
  if (!address || typeof address !== "string" || address.trim() === "") {
    return "No Address";
  }

  const addressPrefix = "Adresse :";
  let cleanAddress = address;
  if (address.startsWith(addressPrefix)) {
    cleanAddress = address.slice(addressPrefix.length).trim();
  }

  cleanAddress = cleanAddress.replace(/\n/g, ", ").replace(/\s+/g, " ").trim();
  const words = cleanAddress.split(/[\s,]+/).filter(Boolean);
  if (words.length <= wordLimit) return cleanAddress;
  return words.slice(0, wordLimit).join(" ") + "...";
};

// Glovo Logo Component
const GlovoLogo = ({ onClick }) => {
  return (
    <div className="glovo-logo" onClick={onClick}>
      <div className="glovo-icon">
        <img 
          src="https://fr.le360.ma/resizer/v2/TQLEV45SH5GF3FOQFI6VHSK3NU.png?auth=d068f883d95fa5a6e00a795f557b9ba77a2a03faf73722f00ede1e4a9093a508&smart=true&width=1216&height=684"
          alt="Glovo"
          className="glovo-image"
        />
      </div>
    </div>
  );
};

const TaskSidebar = ({
  className = "",
  tasks,
  isVisible,
  setIsVisible,
  onTaskClick,
  onAssignSuccess,
  onDrawLine,
  onRemoveLines,
  updateTaskOptimistically,
  updateDriverOptimistically,
}) => {
  const [activeTab, setActiveTab] = useState("nonaccepter");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedTaskUuid, setSelectedTaskUuid] = useState(null);

  // Debug task categorization (empty loops for debugging, can be removed if not needed)
  useMemo(() => {
    tasks.prete.forEach((task) => {});
    tasks.completed.forEach((task) => {});
  }, [tasks.prete, tasks.completed]);

  const hasPreteTasks = useMemo(() => {
    const allPreteTasks = [
      ...tasks.prete,
      ...tasks.completed.filter(
        (task) => task.prete === 1 && !["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      ),
    ];
    return allPreteTasks.length > 0;
  }, [tasks.prete, tasks.completed]);

  const hasUnassignedTasks = useMemo(() => {
    return tasks.unassigned.length > 0;
  }, [tasks.unassigned]);

  const tabs = useMemo(() => {
    const allPreteTasks = [
      ...tasks.prete,
      ...tasks.completed.filter(
        (task) => task.prete === 1 && !["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      ),
    ];
    const allCompletedTasks = [
      ...tasks.completed.filter((task) =>
        ["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      ),
      ...tasks.prete.filter(
        (task) => task.prete === 1 && ["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      ),
    ];

    return [
      { id: "nonaccepter", label: "NONACCEPTER", count: tasks.nonaccepter.length },
      { id: "unassigned", label: "UNASSIGNED", count: tasks.unassigned.length },
      { id: "assigned", label: "ASSIGNIER", count: tasks.assigned.length },
      { id: "prete", label: "PRETE", count: allPreteTasks.length },
      { id: "completed", label: "COMPLETED", count: allCompletedTasks.length },
    ];
  }, [tasks.nonaccepter, tasks.unassigned, tasks.assigned, tasks.prete, tasks.completed]);

  const filteredTasks = useMemo(() => {
    let currentTasks = [];

    if (activeTab === "prete") {
      const preteTasks = tasks.prete.filter(
        (task) => task.prete === 1 && !["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      );
      const misplacedCompletedTasks = tasks.completed.filter(
        (task) => task.prete === 1 && !["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      );
      currentTasks = [...preteTasks, ...misplacedCompletedTasks];
    } else if (activeTab === "completed") {
      const completedTasks = tasks.completed.filter((task) =>
        ["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      );
      const preteCompletedTasks = tasks.prete.filter(
        (task) => task.prete === 1 && ["FAILED", "SUCCESS"].includes(task.status_delivery?.toUpperCase())
      );
      currentTasks = [...completedTasks, ...preteCompletedTasks];
    } else {
      currentTasks = tasks[activeTab] || [];
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      currentTasks = currentTasks.filter((task) => {
        const nom = (task.nom || "").toLowerCase();
        const prenom = (task.prenom || "").toLowerCase();
        const telephoneClient = (task.clientPhone || task.telephone_client || "").toLowerCase();
        const adresseDelivery = (task.adresse_delivery || "").toLowerCase();
        const designation = (task.designation || "").toLowerCase();
        const orderId = (task.order_id || "").toString().toLowerCase();
        const livreurAffecte = (task.livreur_affecte || "").toLowerCase();

        return (
          nom.includes(searchLower) ||
          prenom.includes(searchLower) ||
          telephoneClient.includes(searchLower) ||
          adresseDelivery.includes(searchLower) ||
          designation.includes(searchLower) ||
          orderId.includes(searchLower) ||
          livreurAffecte.includes(searchLower)
        );
      });
    }

    // Sort by creation time (newest first)
    currentTasks.sort((a, b) => {
      const aTime = new Date(a.created_at);
      const bTime = new Date(b.created_at);
      return bTime - aTime;
    });

    return currentTasks;
  }, [tasks, activeTab, searchTerm]);

  const getStatusClass = useCallback((status) => {
    const normalizedStatus = status?.toString().toUpperCase();
    switch (normalizedStatus) {
      case "0":
      case "1":
        return "status-nonaccepter";
      case "2":
        return "status-unassigned";
      case "3":
        return "status-assigned";
      case "4":
        return "status-prete";
      case "5":
        return "status-completed";
      case "SUCCESS":
        return "status-green";
      case "FAILED":
        return "status-red";
      case "NON ASSIGNE":
        return "status-gray";
      case "START":
        return "status-blue";
      case "ACCEPTE":
        return "status-purple";
      case "ASSIGNE":
        return "status-orange";
      case "IN PROGRESS":
        return "status-in-progress";
      default:
        return "status-unknown";
    }
  }, []);

  const isTaskNotAccepted = (task) => {
    return task.status_commande?.toString() === "3" && task.status_pickup?.toUpperCase() !== "ACCEPTE";
  };

  const isTaskUnassigned = (task) => {
    return !task.livreur_affecte || task.livreur_affecte.trim() === "" || task.livreur_affecte.toLowerCase() === "not assigned";
  };

  const handleTaskClick = useCallback(
    (taskOrderId) => {
      onTaskClick(taskOrderId);
    },
    [onTaskClick]
  );

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSearchToggle = useCallback(() => {
    setIsSearchActive(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    if (searchTerm === "") setIsSearchActive(false);
  }, [searchTerm]);

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  const handleOpenAssignModal = useCallback(
    (orderId) => {
      const task = filteredTasks.find((t) => t.order_id === orderId);
      if (task) {
        setSelectedOrderId(orderId);
        setSelectedTaskUuid(task.uuid_commande);
        setIsAssignModalOpen(true);
      } else {
        console.error(`Task with order_id ${orderId} not found in filteredTasks`);
      }
    },
    [filteredTasks]
  );

  const handleCloseAssignModal = useCallback(() => {
    setIsAssignModalOpen(false);
    setSelectedOrderId(null);
    setSelectedTaskUuid(null);
  }, []);

  const handleAssignSuccess = useCallback(() => {
    onAssignSuccess();
  }, [onAssignSuccess]);

  const handleDrawLine = useCallback(
    (task) => {
      if (task.latitude_pickup && task.longitude_pickup && task.latitude_delivery && task.longitude_delivery) {
        const isRequest = task.order_id?.startsWith("R-");
        onDrawLine({
          id: task.id || task.idcmd,
          pickup: { lat: parseFloat(task.latitude_pickup), lng: parseFloat(task.longitude_pickup) },
          delivery: { lat: parseFloat(task.latitude_delivery), lng: parseFloat(task.longitude_delivery) },
          filter: task.filter || activeTab,
          isRequest,
        });
      }
    },
    [onDrawLine, activeTab]
  );

  const handleRemoveLines = useCallback(() => {
    onRemoveLines();
  }, [onRemoveLines]);

  const handleNotifyRestaurant = useCallback(
    (orderId) => {
      sendRestaurantNotification(orderId);
    },
    []
  );

  // Function to determine if Glovo logo should be shown
  const shouldShowGlovoLogo = useCallback((task) => {
    // Show Glovo logo if:
    // 1. trackingNumber has a value (not null or empty)
    // 2. status_pickup is not "START"
    return task.trackingNumber && 
           task.trackingNumber.trim() !== "" && 
           task.status_pickup?.toUpperCase() !== "START";
  }, []);

  return (
    <div className={`task-sidebar ${className}`}>
      <h2>Tâches</h2>
      <div className="task-controls">
        <button className="removeline" title="Supprimer toutes les lignes" onClick={handleRemoveLines}>
          <i className="fas fa-trash"></i>
        </button>
        <div className="search-container">
          {!isSearchActive ? (
            <button className="search-toggle" onClick={handleSearchToggle} title="Rechercher">
              <i className="fas fa-search"></i>
            </button>
          ) : (
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={handleSearchChange}
              onBlur={handleSearchBlur}
              autoFocus
              className="search-input"
            />
          )}
        </div>
      </div>

      <div className="filters-content">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`filters__button ${activeTab === tab.id ? "is-active" : ""} ${
              (tab.id === "prete" && hasPreteTasks) || (tab.id === "unassigned" && hasUnassignedTasks)
                ? "blink-red"
                : ""
            }`}
            onClick={() => handleTabClick(tab.id)}
          >
            <span className="tab-count">{tab.count}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
      <div className="task-content">
        {tabs.map((tab) => (
          <div key={tab.id} className={`task-tab ${activeTab === tab.id ? "is-active" : ""}`}>
            {filteredTasks.length > 0 ? (
              filteredTasks.map((task) => {
                const distance = calculateDistance(
                  parseFloat(task.latitude_pickup),
                  parseFloat(task.longitude_pickup),
                  parseFloat(task.latitude_delivery),
                  parseFloat(task.longitude_delivery)
                );
                const timeElapsed = calculateTimeElapsed(task.created_at);
                const isRequest = task.order_id?.startsWith("R-");
                const address = truncateAddress(task.adresse_delivery, 3);
                const isTruncated = address.endsWith("...");

                return (
                  <div
                    key={task.id || task.idcmd}
                    className="task-card"
                    onClick={() => handleTaskClick(task.order_id)}
                    style={{ cursor: "pointer" }}
                  >
                    {["unassigned", "assigned", "prete"].includes(activeTab) && (
                      <button
                        className={`assign-driver-button ${isTaskNotAccepted(task) ? "blink-red" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAssignModal(task.order_id);
                        }}
                        title="Assign Driver"
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    )}
                    <button
                      className={`draw-line-button ${isRequest ? "draw-line-button-request" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDrawLine(task);
                      }}
                      title="Draw Route"
                    >
                      <i className="fas fa-route"></i>
                    </button>
                    {activeTab === "nonaccepter" && (
                      <button
                        className="notify-restaurant-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNotifyRestaurant(task.order_id);
                        }}
                        title="Notify Restaurant"
                      >
                        <i className="fas fa-bell"></i>
                      </button>
                    )}
                    <div className="task-header">
                      <div className="task-avatar">
                        {shouldShowGlovoLogo(task) ? (
                          <GlovoLogo 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskClick(task.order_id);
                            }}
                          />
                        ) : (
                          <i className="fas fa-utensils"></i>
                        )}
                      </div>
                      <div className="task-title">
                        <h3>{task.designation || "Unknown Restaurant"}</h3>
                        <span className={`task-status ${getStatusClass(task.status_commande)}`}></span>
                      </div>
                    </div>
                    <div className="task-details">
                      <div className="task-info">
                        <i className="fas fa-hashtag"></i>
                        <span>{task.order_id || "N/A"}</span>
                      </div>
                      <div className="task-info task-info-restaurant">
                        <i className="fas fa-user"></i>
                        <span>{`${task.nom || "Unknown"} ${task.prenom || "Customer"}`}</span>
                      </div>
                      <div className="task-info">
                        <i className="fas fa-money-bill-wave"></i>
                        <span>{task.prix_total || "N/A"} MAD</span>
                      </div>
                      <div className="task-info">
                        <i className="fas fa-ruler"></i>
                        <span>{distance ? `${distance} km` : "N/A"}</span>
                      </div>
                      <div className="task-info task-info-address">
                        <i className="fas fa-map-marker-alt"></i>
                        <span className={`task-address ${isTruncated ? "task-address-truncated" : ""}`}>
                          {address}
                        </span>
                      </div>
                      <div className="task-info">
                        <i className="fas fa-phone"></i>
                        <span>{task.telephone_restaurant || "N/A"}</span>
                      </div>
                      <div className="task-info">
                        <i className="fas fa-clock"></i>
                        <span>{timeElapsed}</span>
                      </div>
                      <div className="task-info">
                        <i className="fas fa-user-check"></i>
                        <span>{task.livreur_affecte || "Not Assigned"}</span>
                      </div>
                      <div className="task-status-group">
                        <div className="task-info">
                          <i className="fas fa-store"></i>
                          <span>Pickup: </span>
                          <span className={getStatusClass(task.status_pickup)}>
                            {task.status_pickup || "N/A"}
                          </span>
                          <span
                            className={`task-status blinking ${getStatusClass(task.status_pickup)}`}
                            style={{ marginLeft: "6px" }}
                          ></span>
                        </div>
                        <div className="task-info">
                          <i className="fas fa-home"></i>
                          <span>Delivery: </span>
                          <span className={getStatusClass(task.status_delivery)}>
                            {task.status_delivery || "N/A"}
                          </span>
                          <span
                            className={`task-status blinking ${getStatusClass(task.status_delivery)}`}
                            style={{ marginLeft: "6px" }}
                          ></span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="no-tasks">No tasks found.</p>
            )}
          </div>
        ))}
      </div>
      <AssignDriverModal
        isOpen={isAssignModalOpen}
        onRequestClose={handleCloseAssignModal}
        orderId={selectedOrderId}
        taskUuid={selectedTaskUuid}
        onAssignSuccess={handleAssignSuccess}
        updateTaskOptimistically={updateTaskOptimistically}
        updateDriverOptimistically={updateDriverOptimistically}
      />
    </div>
  );
};

export default TaskSidebar;